import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { apiJson } from "@/lib/api-client";

const DEBOUNCE_MS = 250;
const MIN_QUERY_LENGTH = 2;

export interface MeetingNoteSearchSnippet {
  text: string;
  /** `[start, end)` ranges of `text` holding a query token. */
  highlights: Array<[number, number]>;
}

interface MeetingNoteSearchResponse {
  matches: Array<{ id: string; snippet: MeetingNoteSearchSnippet | null }>;
}

/**
 * Server-side full text search over meeting notes, including their bodies.
 * Callers combine it with their local Zero data, which keeps working offline:
 * a failed search just adds no matches. `snippets` maps the ID of each match
 * to the text around the hit (null when it matched outside the body).
 */
export function useMeetingNoteSearch(query: string, enabled = true) {
  const trimmed = query.trim();
  const [debounced, setDebounced] = useState(trimmed);

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(trimmed), DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [trimmed]);

  const active = enabled && debounced.length >= MIN_QUERY_LENGTH;
  const { data, isFetching } = useQuery({
    queryKey: ["meeting-note-text-search", debounced],
    queryFn: () =>
      apiJson<MeetingNoteSearchResponse>(
        `/api/meeting-notes/search?q=${encodeURIComponent(debounced)}`,
      ),
    enabled: active,
    retry: false,
  });

  const snippets = useMemo(
    () =>
      new Map(
        (active ? (data?.matches ?? []) : []).map((match) => [
          match.id,
          match.snippet,
        ]),
      ),
    [active, data],
  );

  return {
    snippets,
    isSearching:
      enabled &&
      trimmed.length >= MIN_QUERY_LENGTH &&
      (isFetching || debounced !== trimmed),
  };
}
